import { useCallback, useEffect, useState } from 'react';
import { SocialAuthError, channelFollows, channelStoppedBecause } from '@versesverse/core';
import type { ChannelView } from '@versesverse/core';
import { useLang } from '../i18n/lang';
import { refusalWords } from '../i18n/refusal';
import { useOneAtATime } from './useOneAtATime';

/** What the channels panel reads off the social client. */
export interface ChannelsClient {
  channels(): Promise<ChannelView[]>;
  addChannel(channel: { url: string; nodeIds?: string[] }): Promise<ChannelView>;
  /** Connect a retired one again — the inverse retirement never had. */
  reconnectChannel(channelId: string): Promise<ChannelView>;
  removeChannel(channelId: string): Promise<void>;
}

export interface ChannelsProps {
  client: ChannelsClient;
  /** The passage this panel sits on — what a new channel follows by default. */
  nodeId: string;
  /** Null when nobody is signed in, in which case this renders nothing at all. */
  meId: string | null;
}

/*
 * A LEADER'S OWN DISCORD, CONNECTED (COMMUNITY.md §6, Rule 3).
 *
 * The bridge was built end-to-end on the server — the SSRF gate, the per-reader cap, a credential
 * it refuses to hand back even to the person who gave it, a dead channel that retires itself after
 * five failures — and **no client reached any of it**. So the one integration Rule 3 names existed
 * everywhere except the end a person touches, which is the same as not existing.
 *
 * IT LIVES ON THE PASSAGE, not in a settings page, because that is where the intent arises: a
 * leader looking at Genesis 22 wants their server told when a room opens *here*. A settings page
 * would need a passage picker, which is a second screen to answer a question the reader has already
 * answered by standing where they are standing.
 *
 * It renders NOTHING for a signed-out reader and nothing but a single line for a signed-in one who
 * has no channels — the fewest-controls-visible rule the rest of this UI follows. The paste field
 * appears when asked for, and not before.
 */
export function Channels({ client, nodeId, meId }: ChannelsProps): React.JSX.Element | null {
  const { t } = useLang();
  const [channels, setChannels] = useState<ChannelView[]>([]);
  const [unread, setUnread] = useState(false);
  const [asking, setAsking] = useState(false);
  const [url, setUrl] = useState('');
  const [everywhere, setEverywhere] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  /*
   * ONE ACT PER ROW. The rule below stops a failed re-read being reported as a failed act; it does
   * not reach a DUPLICATE act, and the server answers a second disconnect `no such channel of
   * yours` — a 404 because the first one worked. See `useOneAtATime` for why a piece of state
   * cannot answer this.
   */
  const row = useOneAtATime();

  const refresh = useCallback(async (): Promise<ChannelView[]> => client.channels(), [client]);

  useEffect(() => {
    if (meId === null) return;
    let live = true;
    refresh()
      .then(found => { if (live) { setChannels(found); setUnread(false); } })
      /*
       * NOT AN EMPTY LIST. "No channel connected" is a claim about a leader's own community,
       * and a failed read made it — so somebody whose Discord is already bridged could be told
       * it is not, and connect it a second time. Their community then hears every room twice.
       */
      .catch(() => { if (live) setUnread(true); });
    return () => { live = false; };
  }, [meId, refresh]);

  if (meId === null) return null;

  /**
   * What a channel is listening to, said in the reader's terms rather than in node ids — or that it
   * has STOPPED, which is the state a leader would otherwise never learn about.
   *
   * A channel retires itself after five consecutive failures, which is what a webhook deleted in
   * Discord looks like: 404 for ever. That is the right behaviour — retrying for ever turns a bridge
   * into an outbound-request generator aimed at somebody else's server — but `forNode` then skips
   * it, so it announces NOTHING while this panel showed it exactly as it showed a working one.
   *
   * The server had computed `active: false` and said so in the view all along; this panel simply
   * never read it. A leader whose community stopped hearing about rooms had no way to find out here.
   */
  /*
   * WHY IT STOPPED, and it is two different situations wearing one sentence.
   *
   * Since `retiresOn` was narrowed to 404, 401 and 403, a stopped channel is either **deleted** in
   * Discord or **refused** by it — a 429 is our own pace and a 5xx is their bad minute, and neither
   * retires anything now. The old sentence, *"Stopped — reconnect it"*, is advice that cannot work
   * for the first: a deleted webhook's URL never answers again, and pressing reconnect sends the
   * leader round a loop of five more failures. What they need is to make a new webhook.
   *
   * `unknown` is a channel retired before the status was recorded, and it keeps the old sentence
   * rather than guessing which of the two it was.
   */
  const STOPPED = {
    deleted: 'channels.retiredDeleted',
    refused: 'channels.retiredRefused',
    unknown: 'channels.retired',
  } as const;

  const following = (channel: ChannelView): string => {
    /* core's rule, shared with the phone — and it is the ORDER that is shared, not the words: every
       branch below returns something plausible if `retired` is asked last. */
    switch (channelFollows(channel, nodeId)) {
      case 'retired': return t(STOPPED[channelStoppedBecause(channel.stoppedOn) ?? 'unknown']);
      case 'all': return t('channels.followsAll');
      case 'thisPassage': return t('channels.followsThis');
      case 'somePassages': return t('channels.followsSome', { count: String(channel.nodeIds.length) });
    }
  };

  /**
   * ACT, THEN RE-READ — and never report a failed RE-READ as a failed ACT.
   *
   * All three controls here did `await act(); setChannels(await refresh())` inside one `try`, so a
   * re-read that failed after a connection that worked told the leader **"that did not work"**
   * about something that had. The harm is written twenty lines above, about the list's own read:
   * *"somebody whose Discord is already bridged could be told it is not, and connect it a second
   * time. Their community then hears every room twice."* Here it is worse, because the product
   * says the words that invite the second paste.
   *
   * So the two failures are separated. A failed act is a sentence about the act. A failed re-read
   * is the list saying it could not be read — which this screen already knows how to say.
   */
  const andReRead = async (act: () => Promise<void>): Promise<void> => {
    setProblem(null);
    try {
      await act();
    } catch (caught) {
      /* The server explains a bad URL and a full quota, and a pasted credential is exactly where
         somebody needs to be told which of the two happened.

         A signed-out reader is a KNOWN case rather than a surprise: `refusalWords` logs anything
         that is not a written refusal, so a 401 through it cries wolf in the console. Connecting
         had that branch; reconnecting and disconnecting did not, and now all three share it. */
      if (caught instanceof SocialAuthError) setProblem(t('channels.failed'));
      else setProblem(refusalWords(t, caught, 'channels.failed'));
      return;
    }
    try {
      setChannels(await refresh());
      setUnread(false);
    } catch {
      setUnread(true);
    }
  };

  const connect = async (): Promise<void> => {
    const pasted = url.trim();
    if (!pasted) return;
    setBusy(true);
    try {
      await andReRead(async () => {
        /* An empty list is how the server says "everything" — one representation, so the two
           cannot disagree (`hooks.service.ts` matches on `$size: 0`). */
        await client.addChannel({ url: pasted, nodeIds: everywhere ? [] : [nodeId] });
        setUrl('');
        setAsking(false);
      });
    } finally {
      setBusy(false);
    }
  };

  /** Connect a retired one again — see the button, and `reconnectChannel` in core. */
  const reconnect = async (channel: ChannelView): Promise<void> =>
    row.run(channel.id, () => andReRead(() => client.reconnectChannel(channel.id).then(() => undefined)));

  const disconnect = async (channel: ChannelView): Promise<void> =>
    row.run(channel.id, () => andReRead(() => client.removeChannel(channel.id)));

  return (
    <section className="channels" aria-label={t('channels.heading')}>
      <h4 className="channels-heading">{t('channels.heading')}</h4>

      {unread && <p className="channels-none" role="alert">{t('shared.couldNotRead')}</p>}
      {!unread && channels.length === 0 && !asking && <p className="channels-none">{t('channels.none')}</p>}

      {channels.length > 0 && (
        <ul className="channels-list">
          {channels.map(channel => {
            /* Shut while this row's own act is in flight — the others stay live, because a leader
               with one slow request has not stopped using the panel. */
            const inFlight = row.working(channel.id);
            return (
            <li key={channel.id} className="channels-row">
              {/* The webhook id, never the URL — the URL is the credential and the server does not
                  return it. This is the public half, and enough to tell two channels apart. */}
              <span className="channels-who">{channel.webhookId}</span>
              <span className={channel.active ? 'channels-follows' : 'channels-follows channels-stopped'}>
                {following(channel)}
              </span>
              {/* THE WAY BACK, offered exactly where the row says it stopped. Five failures also
                  looks like an evening of Discord being down, and the streak clears only on a
                  success — so a channel that was never broken retires, and until now the remedy was
                  a ceremony: delete it, find the URL again (we never show it, it is a credential),
                  paste it, re-choose the passages. */}
              {!channel.active && (
                <button
                  type="button"
                  className="channels-reconnect"
                  onClick={() => void reconnect(channel)}
                  disabled={inFlight}
                >
                  {t('channels.reconnect')}
                </button>
              )}
              <button
                type="button"
                className="channels-remove"
                onClick={() => void disconnect(channel)}
                disabled={inFlight}
              >
                {t('channels.disconnect')}
              </button>
            </li>
            );
          })}
        </ul>
      )}

      {!asking && (
        <button type="button" className="channels-add" onClick={() => setAsking(true)}>
          {t('channels.connect')}
        </button>
      )}

      {asking && (
        <form
          className="channels-form"
          onSubmit={event => { event.preventDefault(); void connect(); }}
        >
          <label className="channels-url-label" htmlFor="channel-url">{t('channels.urlLabel')}</label>
          <input
            id="channel-url"
            className="channels-url"
            type="url"
            value={url}
            onChange={event => setUrl(event.target.value)}
            placeholder="https://discord.com/api/webhooks/…"
          />
          <p className="channels-help">{t('channels.urlHelp')}</p>

          {/* Following EVERYTHING is a real mode the server supports, and it is the firehose — so it
              is a deliberate choice rather than the default a paste falls into. */}
          <label className="channels-scope">
            <input
              type="checkbox"
              checked={everywhere}
              onChange={event => setEverywhere(event.target.checked)}
            />
            {everywhere ? t('channels.everyPassage') : t('channels.thisPassage')}
          </label>

          <button type="submit" className="channels-submit" disabled={busy || url.trim() === ''}>
            {busy ? t('channels.connecting') : t('channels.connect')}
          </button>
        </form>
      )}

      {problem !== null && <p className="channels-problem" role="alert">{problem}</p>}
    </section>
  );
}
